import { ROUTES } from 'constants/routes';
import { getFirstIncompleteStep } from 'modules/views/shared/Wizard/utils';

/**
 * CH-4652 — decision logic for resuming onboarding after an already-existing worker logs in.
 *
 * When the duplicate-worker check matches a verified worker, `BasicInformation` sends them to the
 * login screen. Nothing used to tie them to the project/employers picked beforehand, so they landed
 * on My Projects with the project missing entirely. This module decides what to do once they are
 * back, and `useLogin` performs it.
 *
 * Pure on purpose — the React glue lives in `useLogin`, mirroring the
 * `utils/routeGuard.ts` + `hooks/useRouteGuard.ts` split.
 */

/** `cache.project.status` for a closed project — the same check `ConsentFormContext` calls archived. */
const ARCHIVED_PROJECT_STATUS = 3;

/**
 * `workerProjectStatus` on a `CompanyNames` entry. Mirrors `WorkerProjectStatus` in
 * `modules/views/pages/ProjectList/types` — declared locally so this util does not depend on a
 * page, the same way `ARCHIVED_PROJECT_STATUS` above mirrors `ApiProjectStatus.CLOSED`.
 */
const WorkerProjectStatus = {
  PENDING: 0,
  ACTIVE: 1,
} as const;

export interface IResumeEmployer {
  label: string;
  value: string;
}

export interface IResumeClassification {
  isArchived: boolean;
  isOnProject: boolean;
  isInActiveList: boolean;
  isInPendingList: boolean;
  newEmployers: IResumeEmployer[];
  mergedEmployers: IResumeEmployer[];
}

export enum ResumeAction {
  /** Nothing to do — hand the worker to the project list. */
  SKIP = 'SKIP',
  /** Attach the missing employers, then continue into the wizard. */
  ASSIGN = 'ASSIGN',
  /** Already attached with nothing new, but onboarding is unfinished — continue into the wizard. */
  RESUME_ONLY = 'RESUME_ONLY',
}

/**
 * True while a resume is pending *for the worker currently loaded*. The route guard reads this to
 * keep a just-authenticated worker on `/login` until `useLogin` has finished and navigated itself —
 * without it the guard would redirect to My Projects, unmounting the login screen mid-flight.
 *
 * The marker holds the id of the worker picked in the duplicate-check modal, and it must match the
 * worker who actually logged in. The whole store except `loader` is persisted to IndexedDB, so a
 * marker left behind by an abandoned flow survives reloads; keying it to the worker means such a
 * leftover can never attach somebody else to a stale project, nor strand them on the login screen.
 */
export const isResumePending = (onboarding: any, user?: any): boolean => {
  const markedWorkerId = onboarding?.temp?.resumeProjectOnboarding;
  if (!markedWorkerId) return false;
  // `user.workerId` as well as `onboarding.id`, and this matters for timing, not just tidiness.
  // On a worker's own login `authenticateUser.fulfilled` flips `user.isWorker` (and sets
  // `user.workerId`) one tick BEFORE `fetchWorkerDetails` populates `onboarding.id`. Keyed on
  // `onboarding.id` alone this returns false in that window, the guard bounces the worker to
  // My Projects, `<Routes>` unmounts, and the half of the resume that runs from an effect dies
  // with the login screen — verified on QA 2026-09-19.
  return markedWorkerId === onboarding?.id || markedWorkerId === user?.workerId;
};

export const isArchivedProject = (cache: any): boolean => cache?.project?.status === ARCHIVED_PROJECT_STATUS;

/**
 * Whether the worker who just logged in should be resumed onto the pre-selected project.
 *
 * Accepts both session shapes: an admin session driving the tablet (`!user.isWorker`, the worker
 * lives on the onboarding slice) and a worker who self-registered from an invite URL
 * (`user.isWorker`).
 */
export const shouldResume = (state: any): boolean => {
  const { onboarding, user, app } = state || {};

  if (!isResumePending(onboarding)) return false;
  if (!onboarding?.temp?.project?.value) return false;
  if (!(onboarding?.temp?.employers?.length > 0)) return false;
  // Someone must be authenticated for the assign endpoints to be callable at all.
  if (!user?.userId) return false;

  // Offline is BOTH signals, matching how `BasicInformation` computes it. `app.isOnline` alone is
  // not enough: an admin can be in the app's offline mode (`uploadingDetails.isOffline`) with the
  // network still up, and this flow is entirely online — it assigns and reloads over the wire, and
  // none of it belongs in the IndexedDB upload queue.
  const isOffline = app?.isOnline === false || !!onboarding?.uploadingDetails?.isOffline;
  if (isOffline) return false;

  return true;
};

/**
 * Where the pre-selected project sits for this worker, and which employers are missing from it.
 *
 * Everything except `isArchived` comes from `onboarding.assignedCompanies` — the project-scoped
 * `Workers/{id}/project/{projectId}/CompanyNames` payload behind `fetchAssignedCompanies`. Each
 * entry carries a `workerProjectStatus`, and `getWorkerLiteProjects` passes that *same* enum as its
 * `status=` tab parameter, so the mapping to My Projects is structural rather than inferred:
 *
 *   WorkerProjectStatus.ACTIVE  (1)  <->  Current tab   (lite/projects?status=1)
 *   WorkerProjectStatus.PENDING (0)  <->  Pending tab   (lite/projects?status=0)
 *
 * Verified against QA on 2026-09-20 across 5 projects / 18 company rows, including two projects the
 * worker held under both an accepted and an invited company: tab membership derived here agreed
 * with the real `lite/projects` tabs in every case, with no disagreements.
 *
 * Deliberately NOT `lite/projects`. That endpoint is worker-wide and paged, so a worker with more
 * projects than the page size could have the target project fall off the page and be misread as
 * "not on the project", triggering a duplicate assignment. `CompanyNames` is scoped to the one
 * project, needs no paging, and is a single request instead of three. It also does not carry a
 * per-company status on `lite/projects` at all — that field comes back `undefined` there.
 *
 * A worker who has never been onboarded to the project gets `200 []`, not a 404/204 — so
 * `isOnProject` is simply "the list is non-empty" (verified on QA 2026-09-20).
 *
 * The Active/Pending checks are `some()`, not "all accepted": a worker can hold one project under
 * several companies and so legitimately count as both (accepted with A, invited with B).
 */
export const classifyResume = ({ onboarding, cache }: { onboarding: any; cache: any }): IResumeClassification => {
  const selectedEmployers: IResumeEmployer[] = onboarding?.temp?.employers || [];

  // Every company the worker already holds on this project, accepted or merely invited.
  const assignedCompanies: any[] = (onboarding?.assignedCompanies || []).filter((company: any) => company?.id);

  const newEmployers = selectedEmployers.filter(
    employer => !assignedCompanies.some((assigned: any) => assigned.id === employer.value),
  );

  // Old ∪ new. The two sets are disjoint by construction, so no de-duplication is needed.
  const mergedEmployers: IResumeEmployer[] = [
    ...assignedCompanies.map((assigned: any) => ({ label: assigned.name, value: assigned.id })),
    ...newEmployers,
  ];

  return {
    isArchived: isArchivedProject(cache),
    isOnProject: assignedCompanies.length > 0,
    isInActiveList: assignedCompanies.some((c: any) => c.workerProjectStatus === WorkerProjectStatus.ACTIVE),
    isInPendingList: assignedCompanies.some((c: any) => c.workerProjectStatus === WorkerProjectStatus.PENDING),
    newEmployers,
    mergedEmployers,
  };
};

/**
 * The agreed decision table. Order matters: a project can be in both the Active and Pending lists,
 * and in that case it counts as Pending — anything unaccepted means onboarding is still owed, and
 * bouncing the worker to My Projects is the friction CH-4652 is about.
 */
export const getResumeAction = (classification: IResumeClassification): ResumeAction => {
  if (classification.isArchived) return ResumeAction.SKIP; // archived project
  if (!classification.isOnProject) return ResumeAction.ASSIGN; // not on the project at all
  if (classification.newEmployers.length > 0) return ResumeAction.ASSIGN; // active or pending, new employers
  if (classification.isInPendingList) return ResumeAction.RESUME_ONLY; // pending (or both lists), same employers
  return ResumeAction.SKIP; // active, same employers
};

/**
 * The wizard step to land on, reusing the same helper `WorkerStatus` uses to resume a worker
 * (`moveUserToFirstIncompleteStep`). It already returns Create Password when no password is set,
 * otherwise the first incomplete step — including the registration consent sub-steps.
 *
 * Every argument is read from `cache` exactly as `routeGuard.getFirstIncompleteRoute` reads it, and
 * that is the point: the guard re-evaluates the destination the moment we navigate, so any
 * disagreement between the two shows up as an instant redirect away from the step we just picked.
 * `consentAgreements` in particular is optional on `getFirstIncompleteStep` — omitting it compiles
 * cleanly and silently resolves the wrong step for a worker who still owes consent.
 */
export const resolveResumeRoute = ({
  onboarding,
  cache,
  hasNewEmployers,
}: {
  onboarding: any;
  cache: any;
  hasNewEmployers: boolean;
}): string => {
  const step = getFirstIncompleteStep({
    onboarding,
    profileFieldConstraintsMap: cache?.profileFieldConstraintsMap || {},
    canCreateUnverifiedWorkers: cache?.canCreateUnverifiedWorkers || false,
    consentAgreements: cache?.consentAgreements,
  });

  if (step?.route) return step.route;

  // Nothing left to complete. A worker who only gained an employer still has to walk their profile
  // for it, which is what the add-employer flow does elsewhere; otherwise there is nothing to show.
  return hasNewEmployers ? ROUTES.PERSONAL_INFORMATION.path : ROUTES.PROJECT_LIST.path;
};
